import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Fallback UI shown when a descendant throws during render. Can be a static
   * node or a function that receives the thrown error.
   */
  fallback: ReactNode | ((error: Error) => ReactNode);
  /** Optional callback invoked when an error is caught. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Small, reusable error boundary. Renders `fallback` instead of unmounting the
 * parent tree when a descendant throws during render. Use this to keep a page
 * shell visible when one sub-widget (e.g. a map, a chart) fails to initialize.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      const { fallback } = this.props;
      return typeof fallback === 'function' ? fallback(error) : fallback;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
